// Insurance Create/Edit/Renew dialog. Owns form state + validation;
// delegates rendering to InsuranceFormFields. Renew mode pre-fills from
// a previous policy, carrying insuranceType/provider/policyNumber and
// `renewedFromId`, while blanking dates so the user enters fresh ones.

import { useState } from "react";
import Modal from "../../../common/Modal";
import Button from "../../../common/Button";
import Alert from "../../../common/Alert";
import InsuranceFormFields from "./InsuranceFormFields";
import {
  buildEmptyInsuranceFormState,
  stateFromInsurance,
  stateFromRenewSource,
  toCreateInsuranceInput,
  type InsuranceFieldErrors,
  type InsuranceFormState,
} from "./insuranceFormState";
import { isValidIsoDate } from "../shared/isoDateValidation";
import { cleanEvidenceLinks } from "../../../../utils/evidenceLinks";
import useSeededFormState from "../../../../hooks/useSeededFormState";
import type {
  CreateInsuranceInput,
  Insurance,
  UpdateInsuranceInput,
} from "../../../../types";

export type InsuranceFormMode = "create" | "edit" | "renew";

interface InsuranceFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  mode: InsuranceFormMode;
  contractId: string;
  /** Existing record for edit mode, or the source policy for renew mode. */
  source?: Insurance;
  onCreate?: (input: CreateInsuranceInput) => Promise<unknown>;
  onUpdate?: (input: UpdateInsuranceInput) => Promise<unknown>;
  isSubmitting?: boolean;
  providerSuggestions?: string[];
}

function validate(state: InsuranceFormState): InsuranceFieldErrors {
  const errors: InsuranceFieldErrors = {};
  if (state.category === "insurance") {
    if (state.insuranceTypes.length === 0) {
      errors.insuranceTypes = "Select at least one insurance type";
    }
    if (
      state.insuranceTypes.includes("other") &&
      !state.otherInsuranceName.trim()
    ) {
      errors.otherInsuranceName = "Enter a name for the other insurance type";
    }
    if (
      state.insuranceTypes.includes("workers-compensation") &&
      !state.workersCompensationState.trim()
    ) {
      errors.workersCompensationState =
        "Enter the state for workers compensation";
    }
  } else {
    if (!state.subjectName.trim()) {
      errors.subjectName =
        state.category === "licence"
          ? "Licence name is required"
          : "Accreditation name is required";
    }
  }
  if (!state.provider.trim()) errors.provider = "Provider is required";
  if (!isValidIsoDate(state.startDate)) {
    errors.startDate = "Enter a valid start date";
  }
  if (!isValidIsoDate(state.expiryDate)) {
    errors.expiryDate = "Enter a valid expiry date";
  }
  if (
    !errors.startDate &&
    !errors.expiryDate &&
    state.expiryDate < state.startDate
  ) {
    errors.expiryDate = "Expiry date must be on or after start date";
  }
  // Evidence links: friendly inline error so the raw concatenated backend Zod
  // message never surfaces in the error banner. Untouched-default / blank rows
  // are dropped silently by cleanEvidenceLinks; only genuine user intent
  // (a labelled row missing a URL, a URL without a label, or an unsafe scheme)
  // produces an error here.
  const evidenceError = cleanEvidenceLinks(state.evidenceLinks).error;
  if (evidenceError) errors.evidenceLinks = evidenceError;
  return errors;
}

const modeTitle: Record<InsuranceFormMode, string> = {
  create: "Add insurance / licence",
  edit: "Edit insurance / licence",
  renew: "Renew insurance / licence",
};

export default function InsuranceFormModal({
  isOpen,
  onClose,
  mode,
  contractId,
  source,
  onCreate,
  onUpdate,
  isSubmitting,
  providerSuggestions,
}: InsuranceFormModalProps) {
  const [errors, setErrors] = useState<InsuranceFieldErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Re-seed whenever the modal opens for a different target. The modal is
  // eager-mounted (only isOpen toggles) — useSeededFormState resets its
  // guard on close, so reopening with the same key (e.g. create → create)
  // re-seeds instead of showing the previous values.
  const [state, setState] = useSeededFormState<InsuranceFormState>(
    isOpen,
    `${mode}:${source?.id ?? "new"}`,
    () => {
      if (mode === "edit" && source) return stateFromInsurance(source);
      if (mode === "renew" && source) return stateFromRenewSource(source);
      return buildEmptyInsuranceFormState();
    },
    () => {
      setErrors({});
      setSubmitError(null);
    },
  );

  const handleChange = (patch: Partial<InsuranceFormState>) => {
    setState((prev) => ({ ...prev, ...patch }));
    if (Object.keys(patch).some((k) => k in errors)) {
      setErrors((prev) => {
        const next = { ...prev };
        for (const k of Object.keys(patch)) {
          delete next[k as keyof InsuranceFieldErrors];
        }
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const v = validate(state);
    if (Object.keys(v).length > 0) {
      setErrors(v);
      return;
    }
    setSubmitError(null);
    try {
      if (mode === "edit" && source && onUpdate) {
        const input: UpdateInsuranceInput = {
          version: source.version,
          ...toCreateInsuranceInput(state, contractId),
        };
        await onUpdate(input);
      } else if ((mode === "create" || mode === "renew") && onCreate) {
        const base = toCreateInsuranceInput(state, contractId);
        if (mode === "renew" && state.renewedFromId) {
          base.renewedFromId = state.renewedFromId;
        }
        await onCreate(base);
      }
      onClose();
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : "Failed to save insurance.",
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={modeTitle[mode]}
      maxWidth="sm:max-w-2xl"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <InsuranceFormFields
          value={state}
          onChange={handleChange}
          errors={errors}
          idPrefix={`ins-${mode}`}
          disabled={isSubmitting}
          providerSuggestions={providerSuggestions}
        />

        {submitError && <Alert variant="error">{submitError}</Alert>}

        <div className="flex justify-end gap-3 pt-2">
          <Button
            variant="secondary"
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button type="submit" loading={isSubmitting} disabled={isSubmitting}>
            {mode === "edit" ? "Save changes" : "Create"}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
